/**
 * The tasks that name this document (RFC 0319 3b), drawn in the pane chrome.
 *
 * The chip draws the list itself: the same nested rows, and the same › into a
 * task's detail and history, as every other task surface (RFC 0325).
 *
 * The document renderer does not own this surface: source, preview, an app
 * view, and a host editor all sit under the same pane header. Reading the
 * project registry here keeps that one header truthful across every renderer.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo'
import type { Repo } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import { SessionTaskLog } from '@mod/core/model'
import { TaskChip, type TaskPort } from '@mod/sdk/ui'
import { useMemo } from 'react'
import { documentTaskTree } from '../../services/tasks/registry'
import { useDocument } from '../../services/world/repo/useDocument'

export function DocumentTasks({
  projectId,
  documentId,
}: {
  projectId: string
  documentId: string
}) {
  const repo = useRepo() as unknown as Repo
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const tasks = useMemo(
    () => documentTaskTree(projectDoc, documentId),
    [projectDoc, documentId],
  )
  const log = useMemo(
    () => new SessionTaskLog(repo as never, undefined, projectId),
    [repo, projectId],
  )
  const port = useMemo<TaskPort>(
    () => ({
      get: (id) => log.get(id),
      update: (id, patch) => log.update(id, patch),
      subscribe: () => () => {},
    }),
    [log],
  )

  // A completed task is still the status of this document. Hiding the whole
  // chip once the last task completes makes a successful handoff look as if
  // the document was never attached to work at all.
  if (tasks.length === 0) return null

  return (
    <TaskChip
      tasks={tasks}
      port={port}
      popoverLabel="Tasks for this document"
      includeCompleted
      popoverWidth={416}
    />
  )
}
